import { getIcons } from './iconDefinitions';
import {
  ArdoqIcon,
  ArdoqIconCategory,
  AssetTypeWithIcon,
  ComponentTypeIconName,
  IconName,
  ShapeType,
  SpecialAssetTypeWithIcon,
  StandardsSvgIcon,
  StandardsSvgShape,
  SvgIconName,
  SvgIconSpriteName,
  SvgImageName,
  SvgShapeSpriteName,
  SvgSpriteName,
} from './types';
import { getShapes } from './shapeAndMarkerDefinitions';
import {
  AssetType,
  PermissionAccessLevel,
  UseCaseSpecificAssetType,
} from '@ardoq/api-types';
import { standards } from './standards';
import { FontAwesomeIconName, fontAwesomeIcons } from './fontAwesomeIcons';
import { colors } from '@ardoq/design-tokens';
import { Color } from '@ardoq/color-helpers';
import { isTruthy } from '@ardoq/common-helpers';

export const getShapesByCategory = (iconCategory: ArdoqIconCategory) =>
  Object.values(getShapes()).filter(
    ({ category }) => category === iconCategory
  );

export const getIconsByCategory = (iconCategory: ArdoqIconCategory) =>
  Object.values(getIcons()).filter(({ category }) => category === iconCategory);

export const getIcon = (iconId: string | null | undefined): ArdoqIcon =>
  (iconId && getIcons()[iconId]) ||
  getIcons().none || {
    id: 'none',
    label: 'none',
    category: ArdoqIconCategory.Ardoq,
  };

const validIconNames: Set<string> = new Set(Object.values(IconName));
export const isValidIconName = (
  iconName: unknown | null | undefined
): iconName is IconName =>
  typeof iconName === 'string' && validIconNames.has(iconName);

export const isComponentTypeIconName = (
  iconName: unknown | null | undefined
): iconName is ComponentTypeIconName =>
  isSvgIconSpriteName(iconName) || isFontAwesomeIconName(iconName);

export const isFontAwesomeIconName = (
  iconName: unknown | null | undefined
): iconName is FontAwesomeIconName =>
  typeof iconName === 'string' && iconName in fontAwesomeIcons;

const validShapeTypes: Set<string> = new Set(Object.values(ShapeType));
export const isShapeType = (
  shapeName: unknown | null | undefined
): shapeName is ShapeType =>
  typeof shapeName === 'string' && validShapeTypes.has(shapeName);

const standardsShapeIds: Set<string> = new Set(
  Object.values(standards)
    .map(({ shapeId }) => shapeId)
    .filter(isTruthy)
);
export const isStandardsShape = (
  shapeName: unknown | null | undefined
): shapeName is StandardsSvgShape =>
  typeof shapeName === 'string' && standardsShapeIds.has(shapeName);

export const isShapeSpriteName = (
  shapeName: unknown | null | undefined
): shapeName is SvgShapeSpriteName =>
  isShapeType(shapeName) || isStandardsShape(shapeName);

export const isSvgIconName = (
  iconName: unknown | null | undefined
): iconName is SvgIconName =>
  isValidIconName(iconName) &&
  (iconName.startsWith('svg_sprite_') || iconName.startsWith('remix_'));

const standardsIconNames: Set<string> = new Set(
  Object.values(standards).flatMap(({ icon }) => (icon ? [icon] : []))
);

export const isStandardsSvgIconName = (
  iconName: unknown | null | undefined
): iconName is StandardsSvgIcon =>
  typeof iconName === 'string' && standardsIconNames.has(iconName);

const validSvgImageNames: Set<string> = new Set(Object.values(SvgImageName));

export const isSvgImageName = (iconName: unknown): iconName is SvgImageName =>
  typeof iconName === 'string' && validSvgImageNames.has(iconName);

export const isSvgIconSpriteName = (
  iconName: unknown | null | undefined
): iconName is SvgIconSpriteName =>
  isSvgIconName(iconName) ||
  isStandardsSvgIconName(iconName) ||
  isSvgImageName(iconName);

export const isSVGIcon = (
  iconId: string | null | undefined
): iconId is SvgSpriteName => {
  if (!iconId) {
    return false;
  }

  return (
    iconId.startsWith('bpmn') ||
    iconId.startsWith('archimate') ||
    iconId.startsWith('edgy') ||
    iconId.startsWith('svg_sprite') ||
    iconId.startsWith('remix_')
  );
};

export type IconMetadata = {
  iconName: IconName;
  iconColor: Color;
};

const assetTypeToIconMetadata: Record<AssetTypeWithIcon, IconMetadata> = {
  [AssetType.BOOKMARK]: {
    iconName: IconName.BOOKMARK,
    iconColor: colors.red700,
  },
  [AssetType.BROADCAST]: {
    iconName: IconName.BROADCAST,
    iconColor: colors.blue600,
  },
  [AssetType.DASHBOARD]: {
    iconName: IconName.DASHBOARD,
    iconColor: colors.red800,
  },
  [AssetType.FOLDER]: {
    iconName: IconName.FOLDER,
    iconColor: colors.grey700,
  },
  [AssetType.METAMODEL]: {
    iconName: IconName.METAMODEL,
    iconColor: colors.yellow800,
  },
  [AssetType.PRESENTATION]: {
    iconName: IconName.PRESENTATION,
    iconColor: colors.pink700,
  },
  [AssetType.REPORT]: {
    iconName: IconName.DESCRIPTION,
    iconColor: colors.blue600,
  },
  [AssetType.SCENARIO]: {
    iconName: IconName.SCENARIO,
    iconColor: colors.green700,
  },
  [AssetType.SURVEY]: {
    iconName: IconName.SURVEYS,
    iconColor: colors.blue700,
  },
  [AssetType.TRAVERSAL]: {
    iconName: IconName.VIEWPOINT,
    iconColor: colors.pink600,
  },
  [AssetType.VIEWPOINT]: {
    iconName: IconName.VIEWPOINT,
    iconColor: colors.brand400,
  },
  [AssetType.WORKSPACE]: {
    iconName: IconName.WORKSPACE,
    iconColor: colors.brand600,
  },
  [SpecialAssetTypeWithIcon.MANAGED_WORKSPACE]: {
    iconName: IconName.MANAGED_WORKSPACE,
    iconColor: colors.brand800,
  },
  [SpecialAssetTypeWithIcon.IMAGE]: {
    iconName: IconName.COLLECTIONS,
    iconColor: colors.grey900,
  },
  [SpecialAssetTypeWithIcon.DOCUMENT]: {
    iconName: IconName.SURVEYS,
    iconColor: colors.grey900,
  },
  [SpecialAssetTypeWithIcon.UNKNOWN_FILE]: {
    iconName: IconName.MISC,
    iconColor: colors.grey900,
  },
  [SpecialAssetTypeWithIcon.FOLDER_OPENED]: {
    iconName: IconName.OPENED_FOLDER,
    iconColor: colors.grey700,
  },
  [UseCaseSpecificAssetType.PERSPECTIVE]: {
    iconName: IconName.VISIBILITY,
    iconColor: colors.grey700,
  },
  [UseCaseSpecificAssetType.SERVICENOW_CONFIG]: {
    iconName: IconName.INTEGRATION,
    iconColor: colors.grey700,
  },
  [UseCaseSpecificAssetType.TABULAR_MAPPING]: {
    iconName: IconName.TABLE_CHART,
    iconColor: colors.grey700,
  },
};

export const getIconMetadataForAssetType = (
  assetType: AssetTypeWithIcon
): IconMetadata => assetTypeToIconMetadata[assetType];

export const getIconNameForAssetType = (
  assetType: AssetTypeWithIcon
): IconName => assetTypeToIconMetadata[assetType].iconName;

export const getColorForAssetType = (assetType: AssetTypeWithIcon): Color =>
  assetTypeToIconMetadata[assetType].iconColor;

const permissionLevelIconMap: Record<PermissionAccessLevel, IconName> = {
  [PermissionAccessLevel.READ]: IconName.ACCESS_READ,
  [PermissionAccessLevel.EDIT]: IconName.EDIT,
  [PermissionAccessLevel.ADMIN]: IconName.SETTINGS,
};

export const getPermissionLevelIcon = (
  permissionLevel: PermissionAccessLevel
): IconName => permissionLevelIconMap[permissionLevel];
